import { ppeRequestEmailSchema } from "@asap/shared/schemas/ppe-requests";
import type { Ppe, PpeRequestStatus } from "@asap/shared/utils/supabase.enum";
import { translatePpe, translatePpeRequestStatus } from "@asap/shared/utils/translate";
import type { SortingState } from "@tanstack/vue-table";
import type { TableEmptyStateType } from "~/utils/types";
import { ppes } from "~/supabase.values";

export type PpeRequestTabValue = PpeRequestStatus | "all";

export type PpeRequestTableFilters = {
  search?: string;
  selectedDate?: Date;
  businessUnitIds?: string[];
  wsmTeamMemberIds: string[];
  consultantTeamMemberIds: string[];
  pageSize: number;
  pageIndex: number;
};

export type PpeRequestListFilters = PpeRequestTableFilters & {
  status: PpeRequestTabValue;
  sorting: SortingState;
};

export const ppePickupLocations = ppeRequestEmailSchema.shape.pickup_location.options;

export type PpePickupLocation = (typeof ppePickupLocations)[number];

export type PpeRequestRowActionKind = "mark_as_pending" | "approve" | "refuse";

type PpeRequestItemForLabel = { equipment: { name: string }; attribute: string | null };

export const SINGLE_SIZE_ATTRIBUTE = "US";

export const isPpe = (name: string): name is Ppe => ppes.some((ppe) => ppe === name);

export const formatPpeName = (name: string) => (isPpe(name) ? translatePpe.fr[name] : name);

export const formatPpeItemLabel = (item: PpeRequestItemForLabel) => {
  const size = item.attribute === SINGLE_SIZE_ATTRIBUTE ? "" : (item.attribute ?? "");
  return `${formatPpeName(item.equipment.name)} ${size}`.trim();
};

export const getPpeRequestStatusLabel = (status: PpeRequestStatus) => translatePpeRequestStatus.fr[status];

export const getPpeRequestStatusColor = (status: PpeRequestStatus) => {
  switch (status) {
    case "pending":
      return "yellow";
    case "todo":
    case "to_validate":
      return "red";
    case "approved":
      return "success";
    case "refused":
      return "gray";
  }
};

export const getPpeRequestRowActionKinds = (
  status: PpeRequestStatus,
  canApprovePpeRequest: boolean
): PpeRequestRowActionKind[] => {
  if (!canApprovePpeRequest) return [];
  if (status === "refused") return ["mark_as_pending"];
  if (status === "pending" || status === "todo") return ["approve", "refuse"];
  return [];
};

export const isPpeRequestEditable = (
  request: { status: PpeRequestStatus; owner_id: string | null },
  viewer: { teamMemberId: string | undefined; isCSM: boolean }
) => request.status === "to_validate" && (viewer.isCSM || viewer.teamMemberId === request.owner_id);

export const canSendPpePickupEmails = (status: PpeRequestStatus, isCSM: boolean) =>
  isCSM && status !== "approved" && status !== "refused";

export const getPpeRequestSortOrder = (
  sorting: SortingState
): { column: string; direction: "asc" | "desc" } | undefined => {
  const firstSort = sorting.at(0);
  if (!firstSort) return undefined;
  return { column: firstSort.id, direction: firstSort.desc ? "desc" : "asc" };
};

export const hasActivePpeRequestFilters = (filters: PpeRequestTableFilters) =>
  Boolean(
    filters.search ||
    filters.selectedDate ||
    filters.businessUnitIds?.length ||
    filters.wsmTeamMemberIds.length ||
    filters.consultantTeamMemberIds.length
  );

export const getPpeRequestEmptyStateType = (filters: PpeRequestTableFilters): TableEmptyStateType =>
  hasActivePpeRequestFilters(filters) ? "no-data-with-filters" : "no-data";
